// src/features/owner/components/ConfirmActionModal.tsx — confirm gate for destructive owner actions.
// Category-requiring actions (suspend/ban/remove) surface a required <select> + optional note.
import { useState } from 'react';
import styled from 'styled-components';
import { Dialog } from '@/ui/Dialog';
import { Button } from '@/ui';
import { MODERATION_CATEGORIES, CATEGORY_LABELS } from '@/core/models/case';
import type { ModerationCategory } from '@/core/models';

/** Actions that must carry one of the five moderation categories. */
const CATEGORY_REQUIRED_ACTIONS = new Set(['suspend', 'ban', 'remove-post']);

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 12px;
`;

const FieldLabel = styled.label`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Select = styled.select`
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 8px 12px;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  outline: none;
  &:focus-visible {
    border-color: ${({ theme }) => theme.color.accentCyan};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.focusRing};
  }
`;

const NoteArea = styled.textarea`
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 10px 12px;
  min-height: 68px;
  resize: vertical;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  outline: none;
  &:focus-visible {
    border-color: ${({ theme }) => theme.color.accentCyan};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.focusRing};
  }
`;

export interface ConfirmActionModalProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  /** Action key — determines whether the category/note fields are shown. */
  actionKind?: string;
  onConfirm: (category?: ModerationCategory, note?: string) => void;
  onClose: () => void;
}

export function ConfirmActionModal({
  open,
  title,
  body,
  confirmLabel,
  actionKind,
  onConfirm,
  onClose,
}: ConfirmActionModalProps) {
  const needsCategory = actionKind ? CATEGORY_REQUIRED_ACTIONS.has(actionKind) : false;
  const [category, setCategory] = useState<ModerationCategory>(MODERATION_CATEGORIES[0]);
  const [note, setNote] = useState('');

  if (!open) return null;

  function handleConfirm() {
    onConfirm(needsCategory ? category : undefined, note.trim() || undefined);
    onClose();
    // reset local state after close
    setCategory(MODERATION_CATEGORIES[0]);
    setNote('');
  }

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <p>{body}</p>

      {needsCategory && (
        <>
          <FieldWrap>
            <FieldLabel htmlFor="cam-category">Category (required)</FieldLabel>
            <Select
              id="cam-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as ModerationCategory)}
            >
              {MODERATION_CATEGORIES.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </Select>
          </FieldWrap>
          <FieldWrap>
            <FieldLabel htmlFor="cam-note">Note (optional)</FieldLabel>
            <NoteArea
              id="cam-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Internal note for the audit log…"
            />
          </FieldWrap>
        </>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleConfirm}>{confirmLabel}</Button>
      </div>
    </Dialog>
  );
}
